/**
 * Service detail content keyed by service slug.
 * Each service has DE (primary, for SEO) plus EN/ES translations.
 * Markdown is supported in `body` blocks.
 */

const DE = "de";
const EN = "en";
const ES = "es";

const L = (de, en, es) => ({ [DE]: de, [EN]: en, [ES]: es });

export const SERVICE_DETAILS = {
  starlink: {
    icon: "Satellite",
    cover: "https://static.prod-images.emergentagent.com/jobs/5c31e2fe-4abc-4e4f-a3d7-a717d07955ff/images/cdab8a501a0a39d796ab9d632cf8871452080a883a593d3a13e466e157c1d575.png",
    metaTitle: L(
      "Starlink Internet Mallorca · Installation, Beratung & Support",
      "Starlink Internet Mallorca · Installation & support",
      "Starlink Internet Mallorca · Instalación y soporte",
    ),
    metaDescription: L(
      "Professionelle Starlink Installation auf Mallorca: bis 400 Mbit/s, niedrige Latenz, Standortanalyse, Montage und deutschsprachiger Support – ideal für Villen, Fincas und Remote Worker.",
      "Professional Starlink installation on Mallorca: up to 400 Mbps, low latency, site analysis, mounting and German-speaking support.",
      "Instalación profesional Starlink en Mallorca: hasta 400 Mbps, baja latencia, análisis del sitio, montaje y soporte en alemán.",
    ),
    eyebrow: L("Starlink Spezialist auf Mallorca", "Starlink specialist", "Especialista Starlink"),
    title: L(
      "Starlink Internet Mallorca",
      "Starlink Internet Mallorca",
      "Starlink Internet Mallorca",
    ),
    subtitle: L(
      "Schnelles, stabiles Satelliten-Internet für Villen, Fincas, Remote Worker und Streamer – professionell installiert, deutschsprachig betreut.",
      "Fast, stable satellite internet for villas, fincas, remote workers and streamers — professionally installed, German support.",
      "Internet satelital rápido y estable para villas, fincas, teletrabajadores y streamers — instalación profesional, soporte en alemán.",
    ),
    intro: L(
      `Viele Orte auf Mallorca haben bis heute keine zuverlässige Glasfaser- oder DSL-Verbindung. Wer in einer Finca bei **Artà**, **Pollença**, **Sóller** oder im Tramuntana-Gebirge lebt, kennt das Problem: Video-Calls brechen ab, Streaming ruckelt, Cloud-Backups laufen tagelang.

**Starlink** löst das mit einem direkten Satelliten-Uplink von SpaceX. Wir installieren Starlink seit 2023 auf der ganzen Insel und übernehmen alles: Beratung, Hardware-Bestellung, Montage, Konfiguration, Mesh-Router-Anbindung und langfristigen Support.`,
      `Many places on Mallorca still lack reliable fibre or DSL. We install and support Starlink across the island — full service from consult to handover.`,
      `Muchos lugares de Mallorca aún no tienen fibra o DSL fiable. Instalamos y damos soporte Starlink en toda la isla, de la consulta a la entrega.`,
    ),
    benefits: L(
      [
        "Download bis 400 Mbit/s, Upload bis 40 Mbit/s",
        "Latenz 25–40 ms – ideal für Video-Calls, Cloud, Gaming",
        "Funktioniert auch in entlegenen Gebieten ohne DSL/Glasfaser",
        "Streaming-fähig (Twitch, YouTube, Netflix 4K)",
        "Schnelle Installation in unter 2 Stunden",
        "Wetterfeste Montage (Salzluft-resistent)",
      ],
      [
        "Up to 400 Mbps download, 40 Mbps upload",
        "25–40 ms latency — perfect for calls, cloud and gaming",
        "Works in remote areas without DSL or fibre",
        "Streaming-grade (Twitch, YouTube, Netflix 4K)",
        "Fast install — under 2 hours",
        "Weather-proof mount (salt-air resistant)",
      ],
      [
        "Hasta 400 Mbps descarga, 40 Mbps subida",
        "25–40 ms latencia — perfecto para calls, nube y gaming",
        "Funciona en zonas sin DSL o fibra",
        "Apto streaming (Twitch, YouTube, Netflix 4K)",
        "Instalación rápida — menos de 2 horas",
        "Montaje resistente a clima y aire salino",
      ],
    ),
    sections: L(
      [
        {
          title: "Standort-Analyse vor der Bestellung",
          body: `Wir prüfen kostenfrei aus der Ferne mit Sat-Tools und vor Ort:

- Freie Sicht zum Himmel (mindestens 100°-Kegel)
- Mögliche Schattenwürfe durch Bäume, Berge, Nachbargebäude
- Strom- und LAN-Routing zur Villa
- Optimaler Montagepunkt (Dach, Mast, Garten, Pool-Haus)

So vermeiden Sie teure Fehlkäufe und schlechte Performance.`,
        },
        {
          title: "Welche Starlink-Hardware passt?",
          body: `Wir wählen mit Ihnen das richtige Modell:

- **Starlink Standard** – für Villen, Familien, Homeoffice (ca. 220 Mbit/s)
- **Starlink Performance** – für Streamer, kleine Büros, hohe Auslastung (bis 400 Mbit/s)
- **Starlink Mini** – für Boot, Pool, mobile Setups (bis 100 Mbit/s)
- **Starlink Roam** – für unterwegs (Wohnmobil, Yacht)

Die Hardware bestellen wir auf Ihren Namen oder über unseren Business-Account.`,
        },
        {
          title: "Premium Mesh-WLAN inklusive",
          body: `Starlink liefert das Internet ans Haus – wir verteilen es **stabil in jeden Raum** und an den Pool. Wir kombinieren Starlink mit Ubiquiti-, TP-Link- oder Eero-Mesh-Systemen je nach Größe und Anspruch.`,
        },
        {
          title: "Langfristiger Support",
          body: `Sie bekommen einen festen Ansprechpartner auf Deutsch per WhatsApp. Wir kümmern uns um Updates, Tarifwechsel, Reset bei Problemen und kommen wenn nötig vor Ort.`,
        },
      ],
      [
        { title: "Free site analysis", body: "Sky view, shadow check, power & LAN routing, best mount point." },
        { title: "Right hardware", body: "Standard, Performance, Mini or Roam — we choose with you." },
        { title: "Mesh WiFi included", body: "Starlink to the house, premium mesh to every room and the pool." },
        { title: "Long-term support", body: "One contact via WhatsApp, in German." },
      ],
      [
        { title: "Análisis gratuito", body: "Vista al cielo, sombras, electricidad y cableado." },
        { title: "Hardware adecuado", body: "Standard, Performance, Mini o Roam." },
        { title: "WiFi Mesh incluido", body: "Cobertura premium en toda la villa." },
        { title: "Soporte a largo plazo", body: "Un contacto por WhatsApp." },
      ],
    ),
    faq: L(
      [
        { q: "Wie viel kostet Starlink auf Mallorca?", a: "Hardware ab 349 € einmalig, Tarif ab 50 €/Monat. Unsere Installation beginnt bei 290 €." },
        { q: "Funktioniert Starlink bei Regen oder Sturm?", a: "Ja – moderne Antennen kompensieren leichten Niederschlag. Nur bei starkem Gewitter kann es kurzzeitig Einbußen geben." },
        { q: "Wie lange dauert die Installation?", a: "In der Regel 1,5–2 Stunden vor Ort, sobald die Hardware da ist." },
        { q: "Kann ich Starlink auch für mein Airbnb nutzen?", a: "Absolut – wir richten zusätzlich ein separates Gäste-WLAN mit Bandbreiten-Limit ein." },
      ],
      [
        { q: "Cost?", a: "Hardware from 349 € one-off, plan from 50 €/month. Install from 290 €." },
        { q: "Rain or storm?", a: "Modern dishes handle rain; only severe storms briefly affect bandwidth." },
        { q: "Install time?", a: "About 1.5–2 hours on site." },
        { q: "Airbnb?", a: "Yes – we add a separate guest SSID with bandwidth limits." },
      ],
      [
        { q: "Coste?", a: "Hardware desde 349 € único, tarifa desde 50 €/mes. Instalación desde 290 €." },
        { q: "Lluvia o tormenta?", a: "Las antenas modernas aguantan lluvia; solo tormentas fuertes afectan brevemente." },
        { q: "Tiempo de instalación?", a: "1.5–2 horas in situ." },
        { q: "Para Airbnb?", a: "Sí – añadimos SSID de invitados con límite de ancho de banda." },
      ],
    ),
  },

  wifi: {
    icon: "Wifi",
    cover: "https://images.unsplash.com/photo-1745760469700-b964b4cc8518?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB2aWxsYSUyMG1hbGxvcmNhfGVufDB8fHx8MTc3OTQxMDk1NHww&ixlib=rb-4.1.0&q=85",
    metaTitle: L(
      "Premium WLAN & Mesh-Systeme für Villen auf Mallorca",
      "Premium WiFi & mesh systems for villas on Mallorca",
      "WiFi premium & sistemas mesh para villas en Mallorca",
    ),
    metaDescription: L(
      "Stabiles WLAN in jedem Winkel Ihrer Villa: professionelle Heatmap, Mesh-Systeme von Ubiquiti, TP-Link & Eero, Gäste-Netz und IoT-Trennung – auf Mallorca.",
      "Stable WiFi in every corner: pro heatmap, Ubiquiti/TP-Link/Eero mesh, guest SSID and IoT segregation — Mallorca.",
      "WiFi estable en cada rincón: mapa de calor profesional, Ubiquiti, TP-Link, Eero, SSID de invitados.",
    ),
    eyebrow: L("Mesh-WLAN Spezialist", "Mesh WiFi specialist", "Especialista WiFi mesh"),
    title: L("Premium WLAN & Mesh-Netzwerke", "Premium WiFi & mesh networks", "WiFi premium & redes mesh"),
    subtitle: L(
      "Schluss mit Funklöchern am Pool, ruckelnden Netflix-Abenden und genervten Airbnb-Gästen – wir bauen WLAN, das wirklich überall funktioniert.",
      "End WiFi dead zones at the pool, choppy Netflix evenings and annoyed Airbnb guests.",
      "Adiós a las zonas muertas en la piscina y a los huéspedes Airbnb molestos.",
    ),
    intro: L(
      `Eine 280 m² Villa mit einem einzelnen Router im Wohnzimmer? Garantiert schlechte WLAN-Bewertungen auf Airbnb. Gäste vergeben **bis zu 0,4 Sterne weniger**, wenn das Netz am Pool nicht reicht.

Wir messen Ihre Situation professionell aus und planen das Mesh-Netz mathematisch – nicht "Pi mal Daumen".`,
      `One router in the living room of a 280 m² villa is not enough. We measure and plan properly.`,
      `Un router en el salón de una villa de 280 m² no basta. Medimos y planificamos correctamente.`,
    ),
    benefits: L(
      [
        "Vollständige WLAN-Heatmap mit Profi-Tools (Ekahau, NetSpot)",
        "Mesh-Knoten an mathematisch optimalen Punkten",
        "Separates Gäste-WLAN mit Captive Portal (DSGVO-konform)",
        "IoT-Netz für Kameras & Smart Home (Sicherheit)",
        "Wi-Fi 6 / Wi-Fi 7 wo es Sinn ergibt",
        "Auch für Restaurants, Hotels, Co-Working",
      ],
      ["Full WiFi heatmap", "Mesh nodes at math-optimal spots", "Guest SSID with captive portal", "Separate IoT network", "Wi-Fi 6 / 7 where it makes sense", "Restaurants, hotels, co-working"],
      ["Mapa de calor completo", "Nodos mesh óptimos", "SSID de invitados con portal", "Red IoT separada", "Wi-Fi 6 / 7 cuando tiene sentido", "Restaurantes, hoteles, co-working"],
    ),
    sections: L(
      [
        { title: "Wie wir vorgehen", body: `1. Vor-Ort-Termin und WLAN-Messung (1 Stunde)\n2. Heatmap-Auswertung & Mesh-Plan\n3. Hardware-Empfehlung mit Preisliste\n4. Installation in 1 Tag\n5. Übergabe und Schulung` },
        { title: "Welche Hardware?", body: `- **Ubiquiti UniFi Express + U6-Mesh** – Profi-Niveau, App-Verwaltung\n- **TP-Link Deco BE85** – starkes Wi-Fi 7, einfaches Setup\n- **Eero Pro 6E** – beste UX für Vermieter` },
        { title: "Auswirkung auf Buchungen", body: `Drei Pollença-Hosts berichten 2025 **+12 bis +18 % Buchungen** im Folgejahr – allein durch bessere WLAN-Bewertungen.` },
      ],
      [
        { title: "How we work", body: "On-site measure → heatmap → plan → install → handover." },
        { title: "Hardware", body: "Ubiquiti, TP-Link Deco, Eero." },
        { title: "Bookings", body: "Hosts report +12–18 % bookings after upgrade." },
      ],
      [
        { title: "Cómo trabajamos", body: "Visita in situ → mapa de calor → plan → instalación → entrega." },
        { title: "Hardware", body: "Ubiquiti, TP-Link Deco, Eero." },
        { title: "Reservas", body: "Anfitriones reportan +12–18 % de reservas." },
      ],
    ),
    faq: L(
      [
        { q: "Was kostet eine WLAN-Optimierung?", a: "Ab 290 € Vor-Ort-Termin inkl. Heatmap; Mesh-Sets ab 350 €." },
        { q: "Wie viele Access Points braucht eine 300 m² Villa?", a: "Meist 2–3 Mesh-Knoten plus Hauptrouter." },
        { q: "Geht das auch ohne Kabel-Neuverlegung?", a: "Ja – wir nutzen wireless backhaul oder Powerline wo nötig." },
      ],
      [{ q: "Cost?", a: "From 290 € on-site incl. heatmap. Mesh sets from 350 €." }, { q: "How many APs for 300 m²?", a: "Usually 2–3 mesh nodes + main router." }, { q: "No new cables?", a: "Yes – wireless backhaul or Powerline." }],
      [{ q: "Coste?", a: "Desde 290 € in situ incl. mapa de calor." }, { q: "¿Cuántos APs para 300 m²?", a: "2–3 nodos mesh + router principal." }, { q: "Sin cables nuevos?", a: "Sí – backhaul inalámbrico o Powerline." }],
    ),
  },

  smart: {
    icon: "ShieldCheck",
    cover: "https://images.pexels.com/photos/34989208/pexels-photo-34989208.jpeg",
    metaTitle: L(
      "Smart Home & Sicherheit für Villen auf Mallorca",
      "Smart home & security for Mallorca villas",
      "Smart home & seguridad para villas en Mallorca",
    ),
    metaDescription: L(
      "Smarte Schlösser, Kameras (DSGVO-konform), Alarmanlagen, Energie-Monitoring und Wasser-Sensoren – alles in einer App, eingerichtet auf Mallorca.",
      "Smart locks, GDPR-compliant cameras, alarms, energy monitoring and leak sensors — all in one app.",
      "Cerraduras smart, cámaras RGPD, alarmas, monitorización y sensores de fugas — todo en una app.",
    ),
    eyebrow: L("Smart Home & Sicherheit", "Smart home & security", "Smart home & seguridad"),
    title: L("Smart Home für Ihre Villa", "Smart home for your villa", "Smart home para su villa"),
    subtitle: L(
      "Schlösser, Kameras, Alarm, Klima und Energie – Sie steuern alles aus einer App, egal wo Sie gerade sind.",
      "Locks, cameras, alarm, climate and energy — control everything from one app, anywhere.",
      "Cerraduras, cámaras, alarma, clima y energía — controle todo desde una sola app.",
    ),
    intro: L(
      `Smart Home heißt nicht "noch mehr Apps installieren" – es heißt **eine App, alles drin**. Wir integrieren Geräte verschiedener Hersteller in eine zentrale Steuerung (Home Assistant oder Apple Home), damit Sie und Ihr Verwalter nicht acht Logins brauchen.`,
      `Smart home doesn't mean "more apps" — it means one app, everything inside.`,
      `Smart home no significa "más apps" — significa una app con todo dentro.`,
    ),
    benefits: L(
      [
        "Smarte Türschlösser mit Code pro Gast (Nuki, Yale Linus, Aqara U200)",
        "Außenkameras mit lokaler NVR-Aufzeichnung (DSGVO-konform)",
        "Alarmsysteme mit App-Anbindung und 24/7-Monitoring",
        "Energie-Monitoring – spart oft 20–35 % Stromkosten",
        "Wasser-Leckage-Sensoren (Wasserschaden = oft >6.000 €)",
        "Anwesenheits-Simulation für die Nebensaison",
      ],
      ["Smart locks with per-guest codes", "Outdoor cameras with local NVR", "Alarms with app and monitoring", "Energy monitoring", "Leak sensors", "Presence simulation"],
      ["Cerraduras con códigos por huésped", "Cámaras exteriores con NVR local", "Alarmas con app y monitoring", "Monitorización energética", "Sensores de fugas", "Simulación de presencia"],
    ),
    sections: L(
      [
        { title: "DSGVO & Datenschutz", body: `Wir verbauen **keine** US-Cloud-Kameras im Außenbereich. Aufzeichnung läuft lokal auf NVR oder Synology – Sie sind rechtlich auf der sicheren Seite.` },
        { title: "Für Airbnb-Hosts", body: `Self-Check-in mit zeitbegrenzten Codes pro Buchung, Protokoll wer wann reinkam, automatische Code-Generierung über Smoobu oder Hostex.` },
        { title: "Eine App für alles", body: `Apple Home, Google Home oder Home Assistant – wir richten ein, was zu Ihrem Lebensstil passt.` },
      ],
      [
        { title: "GDPR", body: "Local NVR — no US cloud cameras." },
        { title: "Airbnb hosts", body: "Self check-in with auto codes." },
        { title: "One app", body: "Apple Home, Google Home or Home Assistant." },
      ],
      [
        { title: "RGPD", body: "NVR local — sin nube americana." },
        { title: "Airbnb hosts", body: "Self check-in con códigos automáticos." },
        { title: "Una app", body: "Apple Home, Google Home o Home Assistant." },
      ],
    ),
    faq: L(
      [
        { q: "Was kostet ein typisches Smart-Home-Setup?", a: "Einsteiger ab 1.200 €, Premium-Villa 4.000–9.000 € je nach Umfang." },
        { q: "Geht das auch in einer Mietwohnung?", a: "Ja – wir setzen auf rückbaubare Lösungen ohne Bohrlöcher." },
        { q: "Werden die Daten in Spanien gespeichert?", a: "Auf Wunsch ja – lokales NVR oder Synology vor Ort." },
      ],
      [{ q: "Cost?", a: "From 1,200 €; premium 4,000–9,000 €." }, { q: "Rental?", a: "Yes – reversible install possible." }, { q: "Local data?", a: "Yes – local NVR or Synology." }],
      [{ q: "Coste?", a: "Desde 1.200 €; premium 4.000–9.000 €." }, { q: "Alquiler?", a: "Sí – instalación reversible." }, { q: "Datos locales?", a: "Sí – NVR local o Synology." }],
    ),
  },

  digital: {
    icon: "Globe2",
    cover: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGRlc2slMjBzZXR1cHxlbnwwfHx8fDE3Nzk0MTA5NTR8MA&ixlib=rb-4.1.0&q=85",
    metaTitle: L(
      "Webseiten, SEO & KI-Automation für Unternehmen auf Mallorca",
      "Websites, SEO & AI automation for businesses on Mallorca",
      "Webs, SEO y automatización IA para empresas en Mallorca",
    ),
    metaDescription: L(
      "Moderne Webseiten, Buchungssysteme, SEO, Google Maps Optimierung, KI-Chatbots und Automationen – maßgeschneidert für deutschsprachige Unternehmer auf Mallorca.",
      "Modern websites, booking systems, SEO, Google Maps, AI chatbots and automations.",
      "Webs modernas, sistemas de reservas, SEO, Google Maps, chatbots IA y automatización.",
    ),
    eyebrow: L("Digitale Services", "Digital services", "Servicios digitales"),
    title: L("Webseiten, SEO & KI-Automation", "Websites, SEO & AI automation", "Webs, SEO y automatización IA"),
    subtitle: L(
      "Sichtbarkeit auf Google. Buchungen ohne Kommissionen. Prozesse, die nachts laufen, während Sie schlafen.",
      "Visibility on Google. Bookings without commission. Processes that run while you sleep.",
      "Visibilidad en Google. Reservas sin comisión. Procesos que corren mientras duerme.",
    ),
    intro: L(
      `Deutschsprachige Unternehmer auf Mallorca brauchen drei Dinge: **gefunden werden, schnell antworten, automatisieren**. Wir bauen die digitale Infrastruktur dafür – ohne Theater, ohne Agentur-Aufpreise.`,
      `German entrepreneurs on Mallorca need: get found, reply fast, automate.`,
      `Empresarios alemanes en Mallorca necesitan: ser encontrados, responder rápido, automatizar.`,
    ),
    benefits: L(
      [
        "Webseiten mit moderner Performance (Lighthouse > 90)",
        "Mehrsprachig (DE / EN / ES) standardmäßig",
        "Lokale SEO für 'Restaurant Pollença', 'Villa Mallorca' usw.",
        "Google Maps & Google Business Profil Optimierung",
        "Buchungssysteme ohne Provision (Resmio, SuperSaaS, Cal.com)",
        "KI-Chatbot beantwortet Anfragen 24/7 auf Deutsch",
        "n8n / Make Automationen für Buchhaltung & Lead-Management",
      ],
      ["Modern, fast websites", "Multilingual DE/EN/ES", "Local SEO", "Google Maps optimization", "No-commission bookings", "24/7 AI chatbot", "n8n / Make automations"],
      ["Webs modernas y rápidas", "DE/EN/ES de serie", "SEO local", "Google Maps", "Reservas sin comisión", "Chatbot IA 24/7", "Automatización n8n / Make"],
    ),
    sections: L(
      [
        { title: "Webseite – was ist drin?", body: `- Design auf Ihre Marke abgestimmt\n- Mobile-First, Lighthouse Score >90\n- SEO-Grundgerüst, Sitemap, strukturierte Daten\n- DSGVO-konforme Datenschutzerklärung\n- Hosting + SSL inklusive ersten 12 Monate` },
        { title: "Lokale SEO auf Mallorca", body: `Wir optimieren für Suchanfragen wie **"Starlink Installation Mallorca"**, **"Restaurant Pollença Deutsch"**, **"Smart Home Finca"**. Inklusive Google Business Profile, Mallorca-spezifische Backlinks und KPI-Tracking.` },
        { title: "KI-Chatbot & Automation", body: `Ein Chatbot, der auf Ihrer Webseite Anfragen direkt qualifiziert, Termine vorschlägt und in Ihr CRM einträgt. Wir nutzen GPT-Modelle und n8n.` },
      ],
      [{ title: "Website scope", body: "Custom design, fast, SEO, GDPR, hosting + SSL 12 months." }, { title: "Local SEO", body: "Mallorca-specific search terms and Google Business." }, { title: "AI chatbot", body: "Qualifies leads, books appointments, syncs CRM." }],
      [{ title: "Alcance web", body: "Diseño, rápido, SEO, RGPD, hosting + SSL 12 meses." }, { title: "SEO local", body: "Términos Mallorca y Google Business." }, { title: "Chatbot IA", body: "Cualifica, agenda, sincroniza CRM." }],
    ),
    faq: L(
      [
        { q: "Was kostet eine professionelle Webseite?", a: "Landing-Page ab 1.500 €, Business-Webseite mit CMS 3.500–7.500 €." },
        { q: "Wie lange dauert das?", a: "Landing-Page in 1–2 Wochen, größere Projekte 4–8 Wochen." },
        { q: "Kann ich die Webseite später selbst pflegen?", a: "Ja – Sie bekommen ein einfaches CMS und eine Einweisung." },
      ],
      [{ q: "Cost?", a: "Landing from 1,500 €; CMS site 3,500–7,500 €." }, { q: "Timeline?", a: "Landing 1–2 weeks; bigger projects 4–8 weeks." }, { q: "Self-maintain?", a: "Yes – simple CMS + training." }],
      [{ q: "Coste?", a: "Landing desde 1.500 €; web con CMS 3.500–7.500 €." }, { q: "Plazo?", a: "Landing 1–2 semanas; mayores 4–8." }, { q: "Auto-gestión?", a: "Sí – CMS sencillo + formación." }],
    ),
  },

  creator: {
    icon: "Video",
    cover: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGRlc2slMjBzZXR1cHxlbnwwfHx8fDE3Nzk0MTA5NTR8MA&ixlib=rb-4.1.0&q=85",
    metaTitle: L(
      "Streaming Setups & Kameramann für IRL Streamer auf Mallorca",
      "Streaming setups & camera operator for IRL streamers on Mallorca",
      "Setups streaming & cámara para streamers IRL en Mallorca",
    ),
    metaDescription: L(
      "Mobiles IRL-Streaming mit Starlink, Encoder-Setups, Audio- und Video-Optimierung. Plus: wir arbeiten als Kameramann, damit du als Streamer die Kamera nicht selber halten musst.",
      "Mobile IRL streaming with Starlink, encoders, audio/video setup. Plus: we work as your camera operator on Mallorca.",
      "Streaming IRL móvil con Starlink, encoders, audio/vídeo. Además: trabajamos de cámara para tus streams en Mallorca.",
    ),
    eyebrow: L("Creator, Streamer & Content", "Creator, streamer & content", "Creator, streamer & content"),
    title: L(
      "Creator & Streaming Setups – inkl. Kameramann-Service",
      "Creator & streaming setups – with camera operator",
      "Setups streaming & creator — con cámara",
    ),
    subtitle: L(
      "IRL-Streaming, Twitch, YouTube und Reels in TV-Qualität auf Mallorca. Wir bauen die Technik – und stehen bei Bedarf als Kameramann hinter dir.",
      "IRL streaming, Twitch, YouTube and Reels in TV quality. We build the tech — and we stand behind the camera when you need us.",
      "Streaming IRL, Twitch, YouTube y Reels en calidad TV. Hacemos la técnica — y operamos la cámara cuando lo necesites.",
    ),
    intro: L(
      `Auf Mallorca zu streamen sieht von außen wie Urlaub aus – in Wahrheit ist es **ein harter Job**. Du brauchst stabile Bandbreite (selbst in der Tramuntana), zuverlässigen Audio-Sound bei Wind, ein Encoder-Setup das nicht abstürzt – **und manchmal jemanden, der dir einfach die Kamera abnimmt**.

Wir kombinieren beides: wir bauen dein Live-Setup mit Starlink, Mesh und Pro-Encodern – und wir stehen auf Wunsch persönlich als Kameramann oder Operator hinter dem Rig.`,
      `Streaming on Mallorca looks like vacation — in reality it's hard work. We build your live setup AND we operate the camera for you when needed.`,
      `Hacer streaming en Mallorca parece vacaciones — en realidad es trabajo duro. Montamos tu setup en directo Y operamos la cámara cuando lo necesitas.`,
    ),
    benefits: L(
      [
        "**Kameramann-Service für IRL Streamer** – du musst die Kamera nicht selber halten",
        "Mobile Setups mit Starlink Mini + LTE-Bonding (99,9 % Uptime)",
        "Pro-Encoder (LiveU, MoneyPit, OBS auf X86) für TV-Qualität",
        "Wireless-Audio (Røde, Sennheiser, DJI Mic) mit Wind-Schutz",
        "Stabilisierte Kameras (Ronin, Osmo, FX3) + Gimbal-Operator",
        "Lichttechnik für Sonnenuntergangs-Streams am Meer",
        "Schnitt, Thumbnails und Repacker für Shorts/Reels",
      ],
      [
        "**Camera operator service for IRL streamers** — no more holding the camera yourself",
        "Mobile Starlink Mini + LTE bonding (99.9% uptime)",
        "Pro encoders (LiveU, MoneyPit, OBS on X86)",
        "Wireless audio (Røde, Sennheiser, DJI Mic) with wind protection",
        "Stabilized cameras + gimbal operator",
        "Lighting for sunset streams",
        "Edit, thumbnails and shorts/reels repacking",
      ],
      [
        "**Cámara para streamers IRL** — no tienes que sujetar la cámara",
        "Setups móviles con Starlink Mini + bonding LTE",
        "Encoders pro (LiveU, MoneyPit, OBS X86)",
        "Audio inalámbrico (Røde, Sennheiser, DJI Mic) con anti-viento",
        "Cámaras estabilizadas + operador de gimbal",
        "Iluminación para streams al atardecer",
        "Edición, miniaturas y shorts/reels",
      ],
    ),
    sections: L(
      [
        {
          title: "Kameramann-Service für IRL Streamer",
          body: `Das ist unser **Alleinstellungsmerkmal**: viele Streamer kommen nach Mallorca, um Content zu machen, und realisieren am ersten Tag wie nervig es ist, **stundenlang die Kamera oder das Phone selber zu halten**.

Wir arbeiten direkt mit dir als **Kameramann / Camera Operator**:

- Wir tragen das Rig (Gimbal, Mikro, Encoder, LTE/Starlink) für dich
- Wir filmen dich aus optimalem Winkel – on the move oder stationär
- Wir kümmern uns um Audio-Pegel, Akku-Wechsel, Cuts in Echtzeit
- Auf Wunsch operieren wir auch eine zweite Kamera (Multicam-Stream)
- Buchbar **pro Stunde, pro Tag oder als Wochen-Tour**

Egal ob Twitch IRL, YouTube Vlog oder Shorts: wir liefern dir die Hände, das Auge und die Technik – du konzentrierst dich auf den Inhalt.`,
        },
        {
          title: "Stationäres Streaming-Setup zuhause / in der Villa",
          body: `Pro-Setup für deine Streaming-Ecke:

- Kamera (Sony FX3 / FX30 oder Lumix S5II) statt Webcam
- Capture-Card + dedizierter Streaming-PC
- Akustik-Behandlung im Raum (Wandpaneele)
- Beleuchtung (Aputure, Godox)
- Software-Setup (OBS, Streamlabs, Restream)`,
        },
        {
          title: "Mobile IRL-Rigs",
          body: `Wir bauen das Rig, mit dem internationale Twitch-Streamer auf Mallorca laufen:

- Backpack mit Starlink Mini + 2× LTE-Modems (bonded)
- Encoder LiveU Solo / MoneyPit
- DJI Osmo Mobile 6 oder Ronin RS3 Mini
- DJI Mic 2 + Lavalier
- 2 × Akkupacks für 6+ Stunden Laufzeit`,
        },
      ],
      [
        { title: "Camera operator for IRL streamers", body: "We carry the rig, frame the shot, handle audio and cuts — book hourly, daily or weekly." },
        { title: "Home setup", body: "Cinema cam, capture card, lighting, acoustic treatment." },
        { title: "Mobile IRL rig", body: "Starlink Mini + bonded LTE + LiveU encoder + Osmo / Ronin." },
      ],
      [
        { title: "Cámara para streamers IRL", body: "Llevamos el rig, hacemos el plano, gestionamos audio y cortes." },
        { title: "Setup en casa", body: "Cámara cine, capturadora, iluminación, acústica." },
        { title: "Rig móvil IRL", body: "Starlink Mini + LTE bonded + LiveU + Osmo / Ronin." },
      ],
    ),
    faq: L(
      [
        { q: "Was kostet ein Kameramann pro Tag?", a: "Halbtag ab 250 €, Tag ab 450 €, Wochen-Pakete günstiger. Inklusive Equipment-Kosten und Strom." },
        { q: "Macht ihr auch Multicam-Streams (mehrere Winkel)?", a: "Ja – Multicam mit OBS und 2 Operators ist kein Problem." },
        { q: "Geht IRL Streaming auch in der Tramuntana?", a: "Mit unserem Starlink-Mini + LTE-Bonded Setup ja, auch in entlegenen Gebieten." },
        { q: "Welche Plattformen unterstützt ihr?", a: "Twitch, YouTube, Kick, TikTok Live, Instagram Live, X Live, Mehrkanal über Restream." },
      ],
      [
        { q: "Camera op cost?", a: "Half-day 250 €, day 450 €, weekly cheaper. Equipment included." },
        { q: "Multicam?", a: "Yes – multi-angle with OBS and 2 operators." },
        { q: "Tramuntana?", a: "Yes – Starlink Mini + bonded LTE handles remote areas." },
        { q: "Platforms?", a: "Twitch, YouTube, Kick, TikTok, IG Live, X, Restream." },
      ],
      [
        { q: "Cámara coste?", a: "Medio día 250 €, día 450 €, semanal más barato." },
        { q: "Multicam?", a: "Sí – multicámara con OBS y 2 operadores." },
        { q: "Tramuntana?", a: "Sí – Starlink Mini + LTE bonded en zonas remotas." },
        { q: "Plataformas?", a: "Twitch, YouTube, Kick, TikTok, IG Live, X, Restream." },
      ],
    ),
  },

  support: {
    icon: "Headphones",
    cover: "",
    metaTitle: L(
      "Deutschsprachiger Tech-Support & Concierge auf Mallorca",
      "German-speaking tech support & concierge on Mallorca",
      "Soporte técnico en alemán & concierge en Mallorca",
    ),
    metaDescription: L(
      "Schnelle Hilfe per WhatsApp, Remote-Support, Vor-Ort-Service auf ganz Mallorca – ein persönlicher Ansprechpartner für Ihre gesamte Technik.",
      "Fast help via WhatsApp, remote support, on-site service across Mallorca — one personal contact for all your tech.",
      "Ayuda rápida por WhatsApp, soporte remoto, servicio in situ — un contacto personal para toda su tecnología.",
    ),
    eyebrow: L("Tech Support & Concierge", "Tech support & concierge", "Soporte técnico & concierge"),
    title: L("Persönlicher Tech-Support auf Deutsch", "Personal tech support in German", "Soporte técnico personal en alemán"),
    subtitle: L(
      "Eine WhatsApp-Nummer, ein Ansprechpartner, alle Themen rund um Ihre Technik auf Mallorca.",
      "One WhatsApp number, one contact, every tech topic on Mallorca.",
      "Un WhatsApp, un contacto, todos los temas técnicos en Mallorca.",
    ),
    intro: L(
      `Sie wollen sich nicht mit drei verschiedenen Servicedesks auf Spanisch auseinandersetzen, wenn das WLAN streikt. Wir nehmen Ihnen das ab.`,
      `You don't want to fight three Spanish service desks when the WiFi dies. We handle that.`,
      `Usted no quiere lidiar con tres mesas de servicio en español. Nosotros lo hacemos.`,
    ),
    benefits: L(
      [
        "Deutschsprachiger Erst-Kontakt per WhatsApp",
        "Remote-Hilfe in der Regel innerhalb 1 Stunde",
        "Vor-Ort-Service auf Mallorca (Palma → Pollença → Artà)",
        "Monatliche Wartungspakete für Villen & Airbnbs",
        "Kommunikation mit spanischen Providern (Movistar, Vodafone, Orange)",
        "Notfall-Hotline (24/7 als Add-on)",
      ],
      ["German-speaking first contact", "Remote help within 1 h", "On-site across Mallorca", "Monthly maintenance", "Dealing with Spanish providers", "24/7 emergency add-on"],
      ["Primer contacto en alemán", "Ayuda remota en 1 h", "Servicio in situ", "Mantenimiento mensual", "Trato con proveedores españoles", "Emergencia 24/7 opcional"],
    ),
    sections: L(
      [
        { title: "Wartungspakete", body: `**Basis** (89 €/Monat): Software-Updates, Backups, Status-Reports, 30 Min Remote-Support.\n\n**Premium** (249 €/Monat): wie Basis + 2 Vor-Ort-Stunden + Priority WhatsApp + monatliche Sicherheits-Audit.\n\n**Concierge** (auf Anfrage): wie Premium + 24/7 Notfall-Hotline + persönlicher Account-Manager.` },
        { title: "Was ist nicht enthalten?", body: `Hardware-Anschaffungen werden separat berechnet (zum Selbstkostenpreis + 10 % Handling). Reparaturen bei Drittanbietern werden in Ihrem Namen organisiert.` },
      ],
      [
        { title: "Maintenance plans", body: "Basic 89 €/mo, Premium 249 €/mo, Concierge custom." },
        { title: "Not included", body: "Hardware at cost + 10 % handling; third-party repairs arranged in your name." },
      ],
      [
        { title: "Planes mantenimiento", body: "Básico 89 €/mes, Premium 249 €/mes, Concierge a medida." },
        { title: "No incluido", body: "Hardware a coste + 10 % handling." },
      ],
    ),
    faq: L(
      [
        { q: "Wie schnell antwortet ihr?", a: "Werktags meist innerhalb von 1 Stunde per WhatsApp." },
        { q: "Kostet die Erst-Beratung etwas?", a: "Nein – das erste 30-Min-Gespräch ist immer kostenfrei." },
        { q: "Welche Insel-Regionen deckt ihr ab?", a: "Ganz Mallorca: Palma, Calvià, Pollença, Sóller, Artà, Cala d'Or, Andratx." },
      ],
      [{ q: "Response time?", a: "Usually within 1 h on weekdays via WhatsApp." }, { q: "First call cost?", a: "Free – first 30 min are on us." }, { q: "Coverage?", a: "All Mallorca." }],
      [{ q: "Tiempo de respuesta?", a: "1 h en días laborables por WhatsApp." }, { q: "Primera llamada?", a: "Gratis – 30 min." }, { q: "Cobertura?", a: "Toda Mallorca." }],
    ),
  },
};

export const SERVICE_SLUGS = Object.keys(SERVICE_DETAILS);
